import { fiscalYearsMatch, canonicalizeFiscalYear } from "./fiscalYearMatch";

/**
 * Program names in Current AOP By Program (SOP rows for Operating Plan Current FY).
 * Membership is by trimmed program name; any Current-FY SOP row counts (including 0 hours).
 */
export function buildCurrentAopProgramNameSet(sopRows, currentFy) {
  const set = new Set();
  const fy = canonicalizeFiscalYear(currentFy) || String(currentFy ?? "").trim();
  if (!fy) return set;
  for (const o of Array.isArray(sopRows) ? sopRows : []) {
    if (!fiscalYearsMatch(o?.fiscal_year, fy)) continue;
    const p = (o?.program != null ? String(o.program) : "").trim();
    if (p) set.add(p);
  }
  return set;
}

/** @param {string|null|undefined} programName @param {Set<string>|null|undefined} aopProgramNames */
export function isProgramNameInCurrentAop(programName, aopProgramNames) {
  if (!aopProgramNames) return true; // conservative: treat as AOP until membership is known
  const name = (programName != null ? String(programName) : "").trim();
  if (!name) return false;
  return aopProgramNames.has(name);
}

/**
 * Record & Close path: whether the event must collect kid hours (Current AOP program).
 * When program_id is set but the name is not resolved yet, treat as AOP so we do not
 * fall through to /closeEvent (status-only) and omit Ops Plan hours.
 *
 * @param {{ programName?: string|null, programId?: string|null, aopProgramNames?: Set<string>|null, programsResolved?: boolean }} args
 */
export function isEventProgramInCurrentAopForClose({
  programName,
  programId,
  aopProgramNames,
  programsResolved = true,
}) {
  const name = programName != null ? String(programName).trim() : "";
  if (!name) {
    const pid = programId != null ? String(programId).trim() : "";
    if (pid && !programsResolved) return true;
    return false;
  }
  return isProgramNameInCurrentAop(name, aopProgramNames);
}

/** Display labels for Current AOP membership (Program Management, etc.). */
export const CURRENT_AOP_COLUMN_LABEL = "Current AOP";
export const CURRENT_AOP_YES = "Yes";
export const CURRENT_AOP_NO = "No";

/**
 * Known membership only — does not apply the conservative "treat as AOP" default.
 * @param {string|null|undefined} programName
 * @param {Set<string>|null|undefined} aopProgramNames
 * @returns {boolean|null} true/false when known; null while membership set is not loaded
 */
export function getProgramCurrentAopMembership(programName, aopProgramNames) {
  if (!aopProgramNames) return null;
  const name = (programName != null ? String(programName) : "").trim();
  if (!name) return false;
  return aopProgramNames.has(name);
}

/**
 * @param {boolean|null|undefined} inCurrentAop
 * @returns {typeof CURRENT_AOP_YES | typeof CURRENT_AOP_NO | "—"}
 */
export function getCurrentAopMembershipLabel(inCurrentAop) {
  if (inCurrentAop == null) return "—";
  return inCurrentAop ? CURRENT_AOP_YES : CURRENT_AOP_NO;
}
